import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import menuReducer from './slices/menuSlice';
import languageReducer from './slices/languageSlice';
import searchReducer from './slices/searchSlice';
import coachReducer from './slices/coachSlice';
import authReducer from './slices/authSlice';
import userReducer from './slices/userSlice';
import apiStatusReducer from './slices/apiStatusSlice';
import categoryReducer from './slices/categorySlice';
import adminReducer from './slices/adminSlice';
import chatReducer from './slices/chatSlice';
import linkedinChatReducer from './slices/linkedinChatSlice';
import AppError from '../models/error';

// Expire süresi
const EXPIRE_TIME = 10 * 60 * 1000; // 10 dakika

// Transform oluştur
const expireTransform = createTransform(
  // State'i localStorage'a kaydetmeden önce
  (inboundState, key) => {
    try {
      return {
        data: inboundState,
        timestamp: new Date().getTime(),
      };
    } catch (error) {
      console.error('Transform inbound error:', error);
      throw AppError.unexpectedError(AppError.ERROR_PERSIST_TRANSFORM_FAILED, error);
    }
  },
  // State'i localStorage'dan okurken
  (outboundState, key) => {
    try {
      if (!outboundState) {
        return undefined;
      }

      const now = new Date().getTime();
      const expire = outboundState.timestamp + EXPIRE_TIME;

      // Süre dolmuşsa boş state döndür
      if (now > expire) {
        return undefined;
      }

      return outboundState.data;
    } catch (error) {
      console.error('Transform outbound error:', error);
      throw AppError.unexpectedError(AppError.ERROR_PERSIST_TRANSFORM_FAILED, error);
    }
  },
  // Sadece newCoach için uygula
  { whitelist: ['newCoach'] },
);

// Search persist config
const searchPersistConfig = {
  key: 'search',
  storage,
  whitelist: ['keyword', 'selectedCategory', 'selectedCity', 'filters', 'result'], // Saklamak istediğimiz search state'leri
  transforms: [expireTransform],
};

// Auth persist config
const authPersistConfig = {
  key: 'auth',
  storage,
  whitelist: ['isAuthenticated', 'authenticationType', 'token'],
  transforms: [expireTransform],
};

// User persist config
const userPersistConfig = {
  key: 'user',
  storage,
  whitelist: ['newUser', 'currentUser'],
  transforms: [expireTransform],
};

// Language persist config
const languagePersistConfig = {
  key: 'language',
  storage,
  whitelist: ['lang'],
};

const persistedSearchReducer = persistReducer(searchPersistConfig, searchReducer);
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedUserReducer = persistReducer(userPersistConfig, userReducer);
const persistedLanguageReducer = persistReducer(languagePersistConfig, languageReducer);

const store = configureStore({
  reducer: {
    menu: menuReducer,
    language: persistedLanguageReducer,
    search: persistedSearchReducer,
    coach: coachReducer,
    auth: persistedAuthReducer,
    user: persistedUserReducer,
    apiStatus: apiStatusReducer,
    category: categoryReducer,
    admin: adminReducer,
    chat: chatReducer,
    linkedinChat: linkedinChatReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Redux persist için gerekli
      errorHandler: (error, getState) => {
        store.dispatch(setError(AppError.unexpectedError(AppError.ERROR_PERSIST_REHYDRATE_FAILED, error)));
      },
    }),
});

export const persistor = persistStore(store, null, (err) => {
  if (err) {
    store.dispatch(setError(AppError.unexpectedError(AppError.ERROR_PERSIST_REHYDRATE_FAILED, err)));
  }
});

export const resetPersistedState = async () => {
  try {
    await persistor.purge();
    window.location.reload();
  } catch (error) {
    store.dispatch(setError(AppError.unexpectedError(AppError.ERROR_PERSIST_STORAGE_FAILED, error)));
  }
};

export default store;
